import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { customerOrderApi } from "../../../../api/customerOrder";
import { Document, Page, pdfjs } from "react-pdf";
import { Alert, Button, Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import HeroSection from "../../../../components/HeroSection.jsx";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

function OrderDetailsPage() {
    const { orderId } = useParams();
    const [orderDetails, setOrderDetails] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const loadOrderDetails = async () => {
            setIsLoading(true);
            setError("");
            try {
                const details = await customerOrderApi.getCustomerOrderDetails(orderId);
                setOrderDetails(details);
            } catch (loadError) {
                console.error("Failed to load order details", loadError);
                const status = loadError?.err?.response?.status;
                if (status === 404) {
                    setError("Order not found. Please check the order ID and try again.");
                } else {
                    setError(loadError?.message || "We could not load this order. Please try again.");
                }
            } finally {
                setIsLoading(false);
            }
        };
        if (orderId) {
            loadOrderDetails();
        } else {
            setError("Order ID is missing.");
            setIsLoading(false);
        }
    }, [orderId]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    if (isLoading) {
        return (
            <Container className="dashboard-shell">
                <HeroSection
                    accent="customer"
                    eyebrow="Order"
                    title="Order Details"
                    subtitle="Loading your order details..."
                />
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading order details...</span>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="dashboard-shell">
                <HeroSection
                    accent="customer"
                    eyebrow="Order"
                    title="Order Details"
                    subtitle="We could not load this order right now."
                />
                <Alert variant="warning" className="my-4">
                    <Alert.Heading>We couldn&apos;t load this order</Alert.Heading>
                    <p className="mb-3">{error}</p>
                    <Button variant="outline-primary" href="/customer/orders">Back to My Orders</Button>
                </Alert>
            </Container>
        );
    }

    if (!orderDetails) {
        return (
            <Container className="dashboard-shell">
                <HeroSection
                    accent="customer"
                    eyebrow="Order"
                    title="Order Details"
                    subtitle="We could not load this order right now."
                />
                <Alert variant="warning">Order details are unavailable right now.</Alert>
            </Container>
        );
    }

    const {
        orderId: id,
        orderedByUsername,
        taxAuthorityName,
        searchType,
        searchTerm,
        productName,
        taxCertPdf,
        taxCertFileName,
        totalPrice,
        totalTax,
        totalBeforeTax,
        totalProductFee,
        totalServiceFee,
        dateCompleted
    } = orderDetails;

    const pdfDataUrl = taxCertPdf ? `data:application/pdf;base64,${taxCertPdf}` : null;

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="customer"
                eyebrow={`Order #${id}`}
                title="Order Details"
                subtitle={`${taxAuthorityName || ""}${dateCompleted ? " • " + new Date(dateCompleted).toLocaleString() : ""}`}
                actions={(
                    <>
                        <Button variant="outline-secondary" href="/customer/orders" className="me-2">
                            Back to My Orders
                        </Button>
                        {pdfDataUrl && (
                            <Button
                                variant="primary"
                                href={pdfDataUrl}
                                download={taxCertFileName}
                            >
                                Download PDF
                            </Button>
                        )}
                    </>
                )}
            />

            <Row className="g-4 mt-1">
                <Col lg={5}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <strong>Order Summary</strong>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless size="sm" className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="text-muted">Ordered By</td>
                                        <td className="text-end">{orderedByUsername}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Tax Authority</td>
                                        <td className="text-end">{taxAuthorityName}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Product</td>
                                        <td className="text-end">{productName}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Search Type</td>
                                        <td className="text-end">{searchType}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Search Term</td>
                                        <td className="text-end">{searchTerm}</td>
                                    </tr>
                                    <tr className="border-top">
                                        <td className="text-muted">Subtotal</td>
                                        <td className="text-end">${totalBeforeTax.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Tax</td>
                                        <td className="text-end">${totalTax.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Service Fee</td>
                                        <td className="text-end">${totalServiceFee.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">Product Fee</td>
                                        <td className="text-end">${totalProductFee.toFixed(2)}</td>
                                    </tr>
                                    <tr className="border-top">
                                        <td><strong>Total</strong></td>
                                        <td className="text-end"><strong>${totalPrice.toFixed(2)}</strong></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={7}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Tax Certificate</strong>
                            {taxCertFileName && (
                                <span className="text-muted small">{taxCertFileName}</span>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {pdfDataUrl ? (
                                <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, height: "600px", overflow: "auto" }}>
                                    <Document file={pdfDataUrl} onLoadSuccess={onDocumentLoadSuccess}>
                                        {Array.from(new Array(numPages), (el, index) => (
                                            <Page
                                                key={`page_${index + 1}`}
                                                pageNumber={index + 1}
                                                width={560}
                                            />
                                        ))}
                                    </Document>
                                </div>
                            ) : (
                                <Alert variant="info" className="mb-0">No PDF available for this order.</Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default observer(OrderDetailsPage);
